import { useCallback, useContext, useState } from 'react'

import { UserContext } from '@Context/User'
import useFetcher from '@Core/Helpers/fetcher'
import { DEFAULT_ERROR_MSG } from '@Constants'
import { encrypt } from '@Core/Utility/crypto'
import {
  postOathToken,
  getUserDetails,
  postSendOTP,
  postUserCreate,
  postSendSignUpOTPAPI,
  postVerifySignUpOTPAPI,
  postSignUpOTPAPI,
  postLogOutAPI,
  deleteAccountAPI,
} from 'API/index'

const useAuth = () => {
  // eslint-disable-next-line no-unused-vars
  const [state, dispatch] = useContext(UserContext)
  const fetcher = useFetcher()
  const [{ loading, error }, setState] = useState({
    error: {},
    loading: false,
  })

  const updateState = useCallback((newObj) => {
    setState((preData) => {
      return { ...preData, ...newObj }
    })
  }, [])

  const loginAPI = useCallback(
    async (authObj, isLoginFalse = false) => {
      if (Boolean(Object.keys(authObj).length)) {
        updateState({ loading: true })
        return postOathToken({
          fetcher,
          body: {
            grant_type: 'password',
            username: authObj?.username || '',
            password: encrypt(authObj?.password), //btoa(authObj?.password || ''),
            encrypted: true,
          },
        })
          .then((data) => {
            if (Boolean(data?.access_token)) {
              dispatch({ type: 'LOGOUT_USER_DATA', payload: {} })
              dispatch({
                type: 'SET_USER_ACCESS',
                payload: { ...data },
              })
              getUserDetails({ fetcher, params: {}, userData: data }).then(
                (res) => {
                  if (res?.id) {
                    dispatch({
                      type: 'SET_USER_DATA',
                      payload: { ...res, isLoginFalse: isLoginFalse },
                    })
                  }
                }
              )
            } else {
              updateState({ error: data.error || DEFAULT_ERROR_MSG })
            }
            return data
          })
          .finally(() => {
            updateState({ loading: false })
          })
      }
    },
    [dispatch, fetcher, updateState]
  )

  const sendOTPAPI = useCallback(
    async (value) => {
      if (value.mobile) {
        updateState({ loading: true })
        return postSendOTP({
          fetcher,
          params: {
            mobileNo: value.mobile,
          },
        })
          .then((res) => {
            return res
          })
          .finally(() => {
            updateState({ loading: false })
          })
      }
    },
    [fetcher, updateState]
  )

  const loginWithOTPAPI = useCallback(
    async (formData) => {
      if (Boolean(Object.keys(formData).length)) {
        updateState({ loading: true })
        return postOathToken({
          fetcher,
          body: {
            grant_type: 'password',
            username: formData?.mobile || '',
            password: formData?.otp || '',
          },
        })
          .then((res) => {
            if (Boolean(res?.access_token)) {
              dispatch({ type: 'LOGOUT_USER_DATA', payload: {} })
              dispatch({
                type: 'SET_USER_ACCESS',
                payload: { ...res },
              })
            }
            return res
          })
          .finally(() => {
            updateState({ loading: false })
          })
      }
    },
    [dispatch, fetcher, updateState]
  )

  const ResetPassword = useCallback(
    async (body) => {
      if (Object.keys(body).length) {
        updateState({ loading: true })
        return postUserCreate({
          fetcher,
          body: body,
        })
          .then((res) => {
            if (body?.id) {
              dispatch({
                type: 'SET_USER_DATA',
                payload: { ...res },
              })
            }
            return res
          })
          .finally(() => updateState({ loading: false }))
      }
    },
    [dispatch, fetcher, updateState]
  )

  const sendSignUpOTPAPI = useCallback(
    async (value) => {
      if (value.mobile) {
        updateState({ loading: true })
        return postSendSignUpOTPAPI({
          fetcher,
          params: {
            mobileNo: value.mobile,
          },
        })
          .then((res) => {
            return res
          })
          .finally(() => {
            updateState({ loading: false })
          })
      }
    },
    [fetcher, updateState]
  )

  const verifySignUpOTPAPI = useCallback(
    async (value) => {
      if (value.id && value.otp) {
        updateState({ loading: true })
        return postVerifySignUpOTPAPI({
          fetcher,
          params: {
            id: value?.id || '',
            otp: value?.otp || '',
          },
        })
          .then((res) => {
            return res
          })
          .finally(() => {
            updateState({ loading: false })
          })
      }
    },
    [fetcher, updateState]
  )

  const signUpAPI = useCallback(
    async (body, params) => {
      if (Object.keys(body).length) {
        updateState({ loading: true })
        return postSignUpOTPAPI({
          fetcher,
          body: body,
          params: params,
        })
          .then((res) => {
            if (body?.id) {
              dispatch({
                type: 'SET_USER_DATA',
                payload: { ...res, isLoginFalse: true },
              })
            }
            return res
          })
          .finally(() => updateState({ loading: false }))
      }
    },
    [dispatch, fetcher, updateState]
  )

  const logoutAPI = useCallback(async () => {
    updateState({ loading: true })
    return postLogOutAPI({
      fetcher,
    })
      .then((res) => {
        return res
      })
      .finally(() => {
        dispatch({ type: 'LOGOUT_USER_DATA', payload: {} })
        updateState({ loading: false })
      })
  }, [dispatch, fetcher, updateState])

  const deleteAccount = useCallback(() => {
    updateState({ loading: true })
    return deleteAccountAPI({
      fetcher,
    })
      .then((res) => {
        if (Boolean(!res?.is_error) && res?.error?.responseCode === 100) {
          dispatch({
            type: 'LOGOUT_USER_DATA',
            payload: {},
          })
        }
        return res
      })
      .finally(() => updateState({ loading: false }))
  }, [dispatch, fetcher, updateState])

  return {
    loading,
    error,
    loginAPI,
    sendOTPAPI,
    loginWithOTPAPI,
    ResetPassword,
    sendSignUpOTPAPI,
    verifySignUpOTPAPI,
    signUpAPI,
    logoutAPI,
    deleteAccount,
  }
}

export default useAuth
